import { environment } from '../../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import 'rxjs/add/operator/map';
import { TableConfig } from '@app/shared/util/table-config.utils';
import { Lift } from '@models/lift.model';
import { Tray } from '@models/tray.model';
import { Slot } from '@models/slot.model';
import { AppUtils } from '@app/shared/util/app.utils';
import { TrayType } from "@models/enums/tray-type.enum";
import { LiftToolFilters } from '../models/lift-tool-filters.model';

@Injectable()
export class LiftsService {
    static readonly API_LIFTS_BASE_PATH = `${environment.baseURL}/api/lifts`;
    static readonly API_BATCHES_BASE_PATH = `${environment.baseURL}/api/batches`;

    lifts = Array(20).fill(new Lift({id: 1, name: 'Some Lift'}));

    constructor(private http: HttpClient) {
    }

    getLifts(config: TableConfig = null) {
        const params = AppUtils.buildPageableParams(config);
        return this.http.get(LiftsService.API_LIFTS_BASE_PATH, {params});
    }

    getLift(id: number, filters: LiftToolFilters = {}) {
        let params = new HttpParams();
        if (filters.toolUID) {
            params = params.set('toolUID', filters.toolUID);
        }
        if (filters.toolName) {
            params = params.set('toolName', filters.toolName);
        }
        if (filters.lineId) {
            params = params.set('lineId', filters.lineId.toString());
        }
        return this.http.get(`${LiftsService.API_LIFTS_BASE_PATH}/${id}`, {params});
    }

    create(lift) {
        return this.http.post(`${LiftsService.API_LIFTS_BASE_PATH}`, lift);
    }

    delete(lift: Lift) {
        return this.http.delete(`${LiftsService.API_LIFTS_BASE_PATH}/${lift.id}`);
    }

    rename(lift, newName) {
        const newLift = {...lift, name: newName};
        return this.http.put(`${LiftsService.API_LIFTS_BASE_PATH}/${lift.id}`, newLift);
    }

    addTray(lift: Lift, positions: number, name: number, trayType: TrayType) {
        return this.http.post(`${LiftsService.API_LIFTS_BASE_PATH}/${lift.id}/trays`,
            {positions, name, type: trayType});
    }

    deleteTray(lift: Lift, tray: Tray) {
        return this.http.delete(`${LiftsService.API_LIFTS_BASE_PATH}/${lift.id}/trays/${tray.id}`);
    }

    patchTray(liftId: number, tray: Tray) {
        return this.http.patch(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${tray.id}`, tray);
    }

    loadTray(liftId: number, trayId: number, filters: LiftToolFilters = {}) {
        let params = new HttpParams();
        if (filters.toolUID) {
            params = params.set('toolUID', filters.toolUID);
        }
        if (filters.toolName) {
            params = params.set('toolName', filters.toolName);
        }
        if (filters.lineId) {
            params = params.set('lineId', filters.lineId.toString());
        }
        return this.http.get(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}`, {params});
    }

    loadSlot(liftId: number, trayId: number, slotId: number, filters: LiftToolFilters = {}) {
        let params = new HttpParams();
        if (filters.toolUID) {
            params = params.set('toolUID', filters.toolUID);
        }
        if (filters.toolName) {
            params = params.set('toolName', filters.toolName);
        }
        if (filters.lineId) {
            params = params.set('lineId', filters.lineId.toString());
        }
        return this.http.get(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}/slots/${slotId}`, {params});
    }

    renameSlot(liftId: number, trayId: number, slot: Slot) {
        return this.http.patch(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}/slots/${slot.id}`, slot);
    }

    deleteSlot(liftId: number, trayId: number, slotId: number) {
        return this.http.delete(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}/slots/${slotId}`);
    }

    addSlot(liftId: number, trayId: number, slot: Slot) {
        return this.http.post(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}/slots`, slot);
    }

    duplicateSlot(liftId: number, trayId: number, slotId: number) {
        return this.http.post(`${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}/slots/duplicate/${slotId}`, slotId);
    }

    copySlot(prevLiftId: number, prevTrayId: number, liftId: number, trayId: number, slotId: number) {
        return this.http.post(
            `${LiftsService.API_LIFTS_BASE_PATH}/${liftId}/trays/${trayId}/slots/${slotId}/copy`,
            {liftId: prevLiftId, trayId: prevTrayId}
        );
    }
}
